/**
 * Catalog retirement — the ONE rule for "may this jobs_catalog row be offered
 * as a NEW selection?" (field test 2026-10-16, Wave 2).
 *
 * Pure, zero imports, so the browser (src/lib/catalog/newJobCatalogQuery.ts)
 * and every Edge function import the SAME module (no byte mirror).
 *
 * Rule for all code:
 *   - NEW selection (pickers, Quick Search, AI candidate lists, AI / import /
 *     label / alias matchers, configuration pickers, public booking offers)
 *     → filter through `onlyOfferable` / `offerableCatalogJobs`.
 *   - LOOKUP by a stored id or code (existing jobs, canned lines, stored DVI
 *     recommendations, bookings already made, learning, metrics, snapshots)
 *     → plain `from('jobs_catalog')`, unfiltered: history must keep resolving.
 *
 * A retired row is never deleted. `superseded_by_code` names the live row that
 * replaces it, for DISPLAY ("retired — use …"). Matchers do NOT silently follow
 * it: a retired code or label coming from AI / RO / CSV text stays UNMATCHED so
 * the advisor picks (founder plan Q3 recommendation — redirecting the 60-min
 * "Turbocharger — R&R" to the 420-min assembly R&R would silently apply a
 * materially different labor estimate).
 */

export const CATALOG_RETIRED_COLUMN = 'retired_at' as const;
export const CATALOG_SUPERSEDED_COLUMN = 'superseded_by_code' as const;
export const CATALOG_RETIREMENT_SELECT = 'retired_at, superseded_by_code' as const;

export interface RetirableCatalogRow {
  code?: string | null;
  retired_at?: string | null;
  superseded_by_code?: string | null;
}

/** Live = offerable as a NEW selection. A row shape without the column (older select) counts as live. */
export function isOfferableCatalogJob(row: RetirableCatalogRow | null | undefined): boolean {
  return !!row && (row.retired_at ?? null) === null;
}

export function isRetiredCatalogJob(row: RetirableCatalogRow | null | undefined): boolean {
  return !!row && (row.retired_at ?? null) !== null;
}

export function offerableCatalogJobs<T extends RetirableCatalogRow>(rows: readonly T[] | null | undefined): T[] {
  return (rows ?? []).filter(isOfferableCatalogJob);
}

/**
 * Structural: any PostgREST filter builder (browser or Deno supabase-js).
 * Appends `retired_at IS NULL`.
 */
export function onlyOfferable<Q extends { is(column: string, value: null): Q }>(query: Q): Q {
  return query.is(CATALOG_RETIRED_COLUMN, null);
}

/**
 * Follow `superseded_by_code` at most `maxHops`, cycle-safe; return the first
 * live row, or null. DISPLAY ONLY ("replaced by …") — never used to silently
 * bind a match.
 */
export function resolveSuccessor<T extends RetirableCatalogRow>(
  row: T | null | undefined,
  byCode: (code: string) => T | null | undefined,
  maxHops = 3,
): T | null {
  if (!row) return null;
  if (isOfferableCatalogJob(row)) return row;
  const seen = new Set<string>();
  if (row.code) seen.add(row.code);
  let current: T | null | undefined = row;
  for (let hop = 0; hop < maxHops; hop++) {
    const next: string | null | undefined = current?.superseded_by_code;
    if (!next || seen.has(next)) return null;
    seen.add(next);
    current = byCode(next);
    if (!current) return null;
    if (isOfferableCatalogJob(current)) return current;
  }
  return null;
}

/** Retirement integrity over a whole catalog (used by the reseed script's validation and the CSV test). */
export interface RetirementIntegrityIssue {
  code: string;
  problem:
    | 'superseded_without_retired'
    | 'superseded_self'
    | 'successor_missing'
    | 'successor_retired'
    | 'retired_at_not_iso';
}

export function retirementIntegrityIssues(rows: readonly RetirableCatalogRow[]): RetirementIntegrityIssue[] {
  const byCode = new Map<string, RetirableCatalogRow>();
  for (const r of rows) if (r.code) byCode.set(r.code, r);
  const issues: RetirementIntegrityIssue[] = [];
  for (const r of rows) {
    const code = r.code ?? '';
    const retired = r.retired_at ?? null;
    const succ = r.superseded_by_code ?? null;
    if (retired !== null && Number.isNaN(Date.parse(retired))) issues.push({ code, problem: 'retired_at_not_iso' });
    if (succ === null) continue;
    if (retired === null) issues.push({ code, problem: 'superseded_without_retired' });
    if (succ === code) { issues.push({ code, problem: 'superseded_self' }); continue; }
    const target = byCode.get(succ);
    if (!target) issues.push({ code, problem: 'successor_missing' });
    else if (!isOfferableCatalogJob(target)) issues.push({ code, problem: 'successor_retired' });
  }
  return issues;
}
